/**
 * Narrows the Research pages to what one document yielded. The search itself is the feature's, and
 * so is whether the list is open: this shows them. The field's height is the caller's, so it can sit
 * next to whichever search bar.
 */
import { ChevronsUpDown, FileText, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FileTypeBadge } from '@/components/documents/DocumentBadges'
import { Button } from '@/components/ui/button'
import { Command, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/utils/cn'

/** A document to filter by. */
export interface DocumentOption {
  id: string
  name: string
  fileType: string | null
}

/** The feature's document search, as the picker drives and shows it. */
export interface DocumentSearch {
  open: boolean
  onOpenChange: (open: boolean) => void
  query: string
  onQueryChange: (query: string) => void
  status: 'searching' | 'ready' | 'failed'
  results: readonly DocumentOption[]
}

export interface DocumentFilterPickerProps {
  /** The chosen document; null shows the whole case. */
  value: DocumentOption | null
  onChange: (document: DocumentOption | null) => void
  search: DocumentSearch
  className?: string
}

function SearchNotice({ status, count }: { status: DocumentSearch['status']; count: number }) {
  const { t } = useTranslation()
  if (status === 'failed') {
    return (
      <p className="px-2 py-6 text-center text-xs text-destructive">
        {t('research.documentFilter.failed')}
      </p>
    )
  }
  if (count > 0) return null
  return (
    <p className="px-2 py-6 text-center text-xs text-muted-foreground">
      {t(
        status === 'searching'
          ? 'research.documentFilter.searching'
          : 'research.documentFilter.empty'
      )}
    </p>
  )
}

export function DocumentFilterPicker({
  value,
  onChange,
  search,
  className,
}: DocumentFilterPickerProps) {
  const { t } = useTranslation()
  const choose = (document: DocumentOption | null) => {
    onChange(document)
    search.onOpenChange(false)
  }

  return (
    <Popover open={search.open} onOpenChange={search.onOpenChange}>
      <PopoverAnchor asChild>
        <div
          className={cn(
            'flex min-w-0 items-center rounded-lg border bg-card transition-colors',
            value || search.open ? 'border-primary/50' : 'border-border hover:border-primary/40',
            search.open && 'ring-2 ring-primary/15',
            className
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={search.open}
              aria-label={t('research.documentFilter.label')}
              title={value?.name}
              className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-lg pl-3 pr-2 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              <FileText
                aria-hidden
                className={cn('size-4 shrink-0', value ? 'text-primary' : 'text-muted-foreground')}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 truncate',
                  value ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {value?.name ?? t('research.documentFilter.all')}
              </span>
              {!value && (
                <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-muted-foreground/60" />
              )}
            </button>
          </PopoverTrigger>
          {value && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('research.documentFilter.clear')}
              onClick={() => onChange(null)}
              className="mr-1.5 text-muted-foreground md:size-7"
            >
              <X aria-hidden className="size-3.5" />
            </Button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="end"
        className="w-[min(26rem,calc(100vw-2rem))] overflow-hidden p-0 max-md:mx-0"
      >
        <Command shouldFilter={false} className="rounded-none! bg-transparent">
          <CommandInput
            value={search.query}
            onValueChange={search.onQueryChange}
            placeholder={t('research.documentFilter.searchPlaceholder')}
          />
          <CommandList className="max-h-72 p-1 pt-1.5">
            {!search.query && (
              <CommandItem
                value="all-documents"
                data-checked={value === null}
                onSelect={() => choose(null)}
              >
                {t('research.documentFilter.all')}
              </CommandItem>
            )}
            {search.results.map(document => (
              <CommandItem
                key={document.id}
                value={document.id}
                data-checked={value?.id === document.id}
                onSelect={() => choose(document)}
              >
                <FileText aria-hidden className="text-muted-foreground" />
                <span title={document.name} className="min-w-0 flex-1 truncate">
                  {document.name}
                </span>
                {document.fileType && <FileTypeBadge fileType={document.fileType} />}
              </CommandItem>
            ))}
            <SearchNotice status={search.status} count={search.results.length} />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
